/**
 * audioNotifications.js
 *
 * Pure-frontend, offline-only logic for the "Audio Notifications"
 * feature. Three concerns live here:
 *
 *   1. Settings persistence (volume, repeat count) in localStorage.
 *   2. The user-picked audio file persisted in IndexedDB.
 *   3. Playback helper that honours volume + repeat count.
 *
 * No server calls. The user's chosen audio never leaves their device.
 *
 * Storage keys:
 *   • `falak.audio.settings.v1`   → { volume: 0..1, repeat: 1|2|3, enabled: bool }
 *   • `falak.audio.lastFired.v1`  → { date: 'YYYY-MM-DD', prayers: { fajr: iso, ... } }
 *                                   Prevents the same prayer from firing twice
 *                                   on the same day, and auto-wipes at midnight.
 *   • IndexedDB `falak-audio` → objectStore `tones` → key `current`
 *                               Holds the Blob of the picked file.
 */

const SETTINGS_KEY = 'falak.audio.settings.v1';
const LAST_FIRED_KEY = 'falak.audio.lastFired.v1';
const DB_NAME = 'falak-audio';
const STORE = 'tones';
const CURRENT_KEY = 'current';

export const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
export const ALLOWED_MIME = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/wave', 'audio/ogg', 'audio/webm'];
export const ALLOWED_EXT = ['.mp3', '.wav', '.ogg'];

/* ------------------------------------------------------------------ Settings */

const DEFAULT_SETTINGS = { volume: 0.65, repeat: 2, enabled: false };

export function getSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw);
    return {
      volume: typeof parsed.volume === 'number' ? Math.max(0, Math.min(1, parsed.volume)) : DEFAULT_SETTINGS.volume,
      repeat: [1, 2, 3].includes(parsed.repeat) ? parsed.repeat : DEFAULT_SETTINGS.repeat,
      enabled: Boolean(parsed.enabled),
    };
  } catch (_) {
    return { ...DEFAULT_SETTINGS };
  }
}

export function setSettings(partial) {
  const current = getSettings();
  const next = { ...current, ...partial };
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
  } catch (_) { /* storage full / disabled — ignore */ }
  return next;
}

/* ---------------------------------------------------------------- IndexedDB */

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Persist the picked audio file.
 * `file` is a File (from <input type="file">) or a Blob with .type set.
 * Returns metadata { name, size, type }.
 */
export async function saveTone(file) {
  if (!file) throw new Error('no_file');
  if (file.size > MAX_FILE_BYTES) throw new Error('too_large');
  const type = (file.type || '').toLowerCase();
  const name = (file.name || 'tone').toLowerCase();
  const extOk = ALLOWED_EXT.some((e) => name.endsWith(e));
  const mimeOk = ALLOWED_MIME.includes(type);
  if (!extOk && !mimeOk) throw new Error('invalid_type');

  const db = await openDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(
      { blob: file, name: file.name, size: file.size, type: file.type || 'audio/mpeg' },
      CURRENT_KEY,
    );
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  return { name: file.name, size: file.size, type: file.type || 'audio/mpeg' };
}

/**
 * Returns the saved tone as { blob, name, size, type } or null.
 */
export async function loadTone() {
  try {
    const db = await openDb();
    const result = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(CURRENT_KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return result;
  } catch (_) {
    return null;
  }
}

export async function clearTone() {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(CURRENT_KEY);
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (_) { /* ignore */ }
}

/* ----------------------------------------------------------------- Playback */

let currentAudio = null;

/**
 * Play the given blob at the given volume, repeated `repeat` times.
 * Returns a stop function so the caller can cancel in-flight playback.
 */
export function playBlob(blob, { volume = 0.65, repeat = 2 } = {}) {
  stopPlayback(); // any previous playback is cancelled
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  audio.volume = Math.max(0, Math.min(1, volume));
  let plays = 0;
  const total = Math.max(1, Math.min(3, repeat));
  audio.addEventListener('ended', () => {
    plays += 1;
    if (plays < total && currentAudio === audio) {
      // small gap between repeats keeps it from sounding glued together
      setTimeout(() => {
        if (currentAudio === audio) audio.play().catch(() => {});
      }, 300);
    } else {
      URL.revokeObjectURL(url);
      if (currentAudio === audio) currentAudio = null;
    }
  });
  currentAudio = audio;
  audio.play().catch(() => { /* autoplay blocked etc. */ });
  return () => {
    if (currentAudio === audio) {
      audio.pause();
      audio.currentTime = 0;
      URL.revokeObjectURL(url);
      currentAudio = null;
    }
  };
}

export function stopPlayback() {
  if (currentAudio) {
    try { currentAudio.pause(); } catch (_) { /* ignore */ }
    currentAudio = null;
  }
}

export function isPlaying() {
  return currentAudio != null;
}

/* ------------------------------------------------------- Prayer-fire tracker
 *
 * Prevents replay when the user leaves the app open past a prayer time.
 * Reset once per calendar day (same pattern as prayerConfirmations).
 */

function todayKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function readFired() {
  try {
    const raw = localStorage.getItem(LAST_FIRED_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function writeFired(obj) {
  try {
    localStorage.setItem(LAST_FIRED_KEY, JSON.stringify(obj));
  } catch (_) { /* ignore */ }
}

export function getTodayFired() {
  const rec = readFired();
  const today = todayKey();
  if (!rec || rec.date !== today) {
    if (rec) writeFired({ date: today, prayers: {} });
    return {};
  }
  return rec.prayers || {};
}

export function markFired(prayer) {
  const rec = readFired();
  const today = todayKey();
  const prayers = rec && rec.date === today ? { ...(rec.prayers || {}) } : {};
  prayers[prayer] = new Date().toISOString();
  writeFired({ date: today, prayers });
}

export function hasFired(prayer) {
  const fired = getTodayFired();
  return Boolean(fired[prayer]);
}
